import { create } from 'zustand'
import type { Topic } from '@/types'
import { api } from '@/services/api'

interface TopicsState {
  topics: Topic[]
  totalCards: number
  isLoading: boolean
  error: string | null
  selectedTopicId: string | null
}

interface TopicsActions {
  // Local state actions
  setTopics: (topics: Topic[]) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  setSelectedTopic: (id: string | null) => void
  incrementTotalCards: (delta: number) => void

  // API actions
  fetchTopics: () => Promise<void>
  addTopic: (data: { id: string; name: string; description?: string }) => Promise<Topic>
  updateTopic: (id: string, updates: { name?: string; description?: string }) => Promise<void>
  deleteTopic: (id: string) => Promise<void>

  // Computed getters
  getTopic: (id: string) => Topic | undefined
  getTopicsSorted: () => Topic[]
}

type TopicsStore = TopicsState & TopicsActions

export const useTopicsStore = create<TopicsStore>((set, get) => ({
  // Initial state - isLoading starts as true until first fetch completes
  topics: [],
  totalCards: 0,
  isLoading: true,
  error: null,
  selectedTopicId: null,

  // Local state actions
  setTopics: (topics) =>
    set({
      topics,
      isLoading: false,
      error: null,
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),

  setSelectedTopic: (id) => set({ selectedTopicId: id }),

  incrementTotalCards: (delta) =>
    set((state) => ({ totalCards: Math.max(0, state.totalCards + delta) })),

  // API actions
  fetchTopics: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.getTopics()
      set({
        topics: response?.topics ?? [],
        totalCards: response?.totalCards ?? 0,
        isLoading: false,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch topics'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  addTopic: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.createTopic(data)
      set((state) => ({
        topics: [...state.topics, response.topic],
        isLoading: false,
      }))
      return response.topic
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create topic'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateTopic: async (id, updates) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.updateTopic(id, updates)
      set((state) => ({
        topics: state.topics.map((t) => (t.id === id ? response.topic : t)),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update topic'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteTopic: async (id) => {
    set({ isLoading: true, error: null })
    try {
      await api.deleteTopic(id)
      set((state) => ({
        topics: state.topics.filter((t) => t.id !== id),
        selectedTopicId: state.selectedTopicId === id ? null : state.selectedTopicId,
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete topic'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  // Computed getters
  getTopic: (id) => get().topics.find((t) => t.id === id),

  getTopicsSorted: () => {
    const topics = get().topics ?? []
    return [...topics].sort((a, b) => {
      // Sort by numeric ID
      const idA = parseInt(a.id, 10)
      const idB = parseInt(b.id, 10)
      if (!isNaN(idA) && !isNaN(idB)) {
        return idA - idB
      }
      return a.id.localeCompare(b.id)
    })
  },
}))
